import { useEffect, useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import Cookies from "js-cookie"
import { useForm } from "react-hook-form"
import { z } from "zod"

import {
  deviceLogin,
  markDeviceAsTrusted,
  sendMfaOtp,
  verifyOtp,
} from "@/lib/api/auth"
import { getUserEmail, isMfaEnabled } from "@/lib/auth"
import { toast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp"

const FormSchema = z.object({
  pin: z.string().min(6, {
    message: "Your one-time password must be 6 characters.",
  }),
})

export function MobileTrustDevice() {
  const navigate = useNavigate()
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [canResend, setCanResend] = useState(false)
  const email = getUserEmail()
  let deviceId = localStorage.getItem(`device_id-${email}`)

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pin: "",
    },
  })

  useEffect(() => {
    const interval = setInterval(() => {
      const otpSetTime = Cookies.get("otp-set-time")

      if (otpSetTime) {
        const otpSetTimestamp = Number(otpSetTime)
        const currentTime = new Date().getTime()
        const expirationTime = 5 * 60 * 1000
        const remaining =
          (otpSetTimestamp + expirationTime - currentTime) / 1000

        setTimeRemaining(remaining > 0 ? remaining : null)
        setCanResend(remaining <= 0)
      } else {
        setTimeRemaining(null)
        setCanResend(true)
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const mutation = useMutation({
    mutationFn: async (otp: string) => {
      await verifyOtp(otp)
    },
    onSuccess: async () => {
      await markDeviceAsTrusted(deviceId as string)
      localStorage.setItem(`device_trusted-${email}`, "true")

      if (isMfaEnabled()) {
        const mfaAuth = Cookies.get(`mfa-auth-${email}`)
        if (mfaAuth === "true") {
          navigate({ to: "/" })
        } else {
          sendMfaOtp()
          navigate({ to: "/mfa-auth" })
        }
      } else {
        navigate({ to: "/" })
      }
    },
    onError: (error) => {
      toast({
        title: "Incorrect OTP",
        description: `${error}`,
        variant: "destructive",
      })
    },
  })

  function onSubmit(data: z.infer<typeof FormSchema>) {
    mutation.mutate(data.pin)
  }

  async function resendCode() {
    if (canResend) {
      try {
        await deviceLogin("mobile")
        deviceId = localStorage.getItem(`device_id-${email}`)

        toast({
          title: "OTP Resent",
          description: "A new code has been sent to your email.",
        })

        setCanResend(false)
      } catch (error) {
        toast({
          title: "Error",
          description:
            "There was an error resending the OTP. Please try again.",
          variant: "destructive",
        })
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-5/6 space-y-0">
        <FormField
          control={form.control}
          name="pin"
          render={({ field }) => (
            <FormItem className="flex flex-col items-center text-center">
              <FormLabel className="font-gilroy-bold text-neutral-800  mb-1 mt-4">Trust Device OTP</FormLabel>
              <FormControl>
                <InputOTP maxLength={6} {...field}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormDescription className="pb-4">
                Please enter the one-time password sent to your email.
              </FormDescription>
              <FormMessage />
              {timeRemaining !== null && (
                <p className="py-2 text-sm text-muted-foreground">
                  Expires in: {Math.ceil(timeRemaining)} seconds
                </p>
              )}
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full rounded-full">
          Submit
        </Button>

        <div className="flex items-center justify-center text-sm text-muted-foreground">
          Didn&apos;t receive a code?{" "}
          <Button
            type="button"
            onClick={resendCode}
            disabled={!canResend}
            variant="link"
            className="text-primary"
          >
            Resend
          </Button>
        </div>
      </form>
    </Form>
  )
}
